import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AdminMetricCard from './components/AdminMetricCard';
import CRMConfigPanel from './components/CRMConfigPanel';
import UserManagementPanel from './components/UserManagementPanel';
import FloorPlanManagementPanel from './components/FloorPlanManagementPanel';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalAdvisors: 0,
    totalLeads: 0,
    monthlySales: 0,
    activeUsers: 0
  });

  const breadcrumbItems = [
    { label: 'Inicio', path: '/', icon: 'Home' },
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield' }
  ];

  useEffect(() => {
    if (!authLoading && isAdmin && userProfile?.id) {
      loadAdminData();
    }
  }, [authLoading, isAdmin, userProfile?.id]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const metricsData = await dashboardService?.getAdminMetrics();
      setMetrics(metricsData);
    } catch (error) {
      console.error('Error loading admin metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando panel de administración...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />

      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />

        {/* Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Panel de Administración
              </h1>
              <p className="text-xs sm:text-sm lg:text-base text-muted-foreground">
                Gestión completa del sistema: usuarios, planos, precios, premios y rangos
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                iconName="Share2"
                onClick={() => navigate('/social-media-marketing-hub')}
                className="text-xs sm:text-sm"
              >
                Marketing Hub
              </Button>
              <Button
                variant="outline"
                iconName="TrendingUp"
                onClick={() => navigate('/advanced-analytics-custom-dashboard-builder')}
                className="text-xs sm:text-sm"
              >
                Analíticas
              </Button>
              <Button
                variant="outline"
                iconName="Database"
                onClick={() => navigate('/lead-data-management-import-center')}
                className="text-xs sm:text-sm"
              >
                Leads
              </Button>
              <Button
                variant="outline"
                iconName="Lock"
                onClick={() => navigate('/security-connection-management')}
                className="text-xs sm:text-sm"
              >
                Seguridad
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Grid - Mobile optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          <AdminMetricCard
            title="Asesores Activos"
            value={metrics?.totalAdvisors}
            icon="Users"
            trend="+3 este mes"
            color="primary"
          />
          <AdminMetricCard
            title="Leads Totales"
            value={metrics?.totalLeads}
            icon="UserPlus"
            trend="+12 esta semana"
            color="success"
          />
          <AdminMetricCard
            title="Ventas Mensuales"
            value={`S/ ${metrics?.monthlySales?.toLocaleString()}`}
            icon="TrendingUp"
            trend="+8% vs mes anterior"
            color="warning"
          />
          <AdminMetricCard
            title="Usuarios Activos"
            value={metrics?.activeUsers}
            icon="Activity"
            trend="75% de asesores"
            color="info"
          />
        </div>

        {/* Floor Plan Management Panel */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <FloorPlanManagementPanel />
        </div>

        {/* Management Panels - Mobile stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
          <CRMConfigPanel />
          <UserManagementPanel />
        </div>

        {/* Quick Actions - Mobile optimized grid */}
        <div className="card p-4 sm:p-5 lg:p-6">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground mb-3 sm:mb-4 flex items-center">
            <Icon name="Zap" size={20} className="mr-2 text-primary" />
            Acciones Rápidas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
            <Button
              variant="outline"
              fullWidth
              iconName="FileEdit"
              onClick={() => navigate('/admin-floor-plan-editor')}
              className="text-xs sm:text-sm"
            >
              Gestionar Planos
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="Users"
              onClick={() => navigate('/crm-lead-management')}
              className="text-xs sm:text-sm"
            >
              Ver CRM
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="Network"
              onClick={() => navigate('/referral-network')}
              className="text-xs sm:text-sm"
            >
              Red de Referidos
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="GitBranch"
              onClick={() => navigate('/dynamic-pipeline-configuration-center')}
              className="text-xs sm:text-sm"
            >
              Pipeline Config
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="Bell"
              onClick={() => navigate('/real-time-notification-communication-hub')}
              className="text-xs sm:text-sm"
            >
              Notifications
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;